import React, { useEffect, useState } from 'react';
import { systemService, ServerDiagnostics, SystemLog, HistoricalLogs } from '../../services/systemService';
import { BarChart3 } from 'lucide-react';

const AnalyticsPage: React.FC = () => {
  const [diagnostics, setDiagnostics] = useState<ServerDiagnostics | null>(null);
  const [systemLogs, setSystemLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [logsLoading, setLogsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [historicalLogs, setHistoricalLogs] = useState<HistoricalLogs | null>(null);
const [historicalLogsLoading, setHistoricalLogsLoading] = useState<boolean>(false);
const [historicalLogsError, setHistoricalLogsError] = useState<string | null>(null);
const [selectedDate, setSelectedDate] = useState<string>('');
const [selectedLevel, setSelectedLevel] = useState<string>('');
const [selectedSource, setSelectedSource] = useState<string>('');
const [availableDates, setAvailableDates] = useState<string[]>([]);
const [availableSources, setAvailableSources] = useState<string[]>([]);

// fetchSystemLogs fonksiyonundan sonra şunu ekleyin (yaklaşık satır 60 civarı)
  
// Geçmiş logları çekme fonksiyonu
const fetchHistoricalLogs = async () => {
  try {
    setHistoricalLogsLoading(true);
    
    // Filtreleri oluştur - boş string olanları undefined'a çevir
    const filters = {
      level: selectedLevel && selectedLevel !== '' ? selectedLevel : undefined,
      source: selectedSource && selectedSource !== '' ? selectedSource : undefined,
      limit: 1000
    };
    
    console.log('Filtreleme yapılıyor:', {
      date: selectedDate,
      filters
    });
    
    const data = await systemService.getHistoricalLogs(selectedDate, filters);
    
    setHistoricalLogs(data);
    setHistoricalLogsError(null);
    
    // Kullanılabilir kaynakları topla
    if (data.logs && data.logs.length > 0) {
      const sources = [...new Set(data.logs.map(log => log.source).filter(Boolean))];
      setAvailableSources(sources);
    }
    
    // Kullanılabilir tarihleri güncelle
    if (data.availableDates) {
      setAvailableDates(data.availableDates);
    }
    
    console.log('Geçmiş loglar yüklendi:', {
      filtrelendi: !!selectedLevel || !!selectedSource,
      log_sayisi: data.logs.length,
      filtreler: data.filters
    });
    
  } catch (err: any) {
    console.error('Geçmiş log kayıtları alınamadı:', err);
    setHistoricalLogsError(err.response?.data?.message || 'Geçmiş log kayıtları alınamadı.');
  } finally {
    setHistoricalLogsLoading(false);
  }
};

// Mevcut useEffect hook'unu şöyle güncelleyin (yaklaşık satır 80 civarı)

// İlk yükleme ve düzenli güncellemeler için
useEffect(() => {
  fetchDiagnostics();
  fetchSystemLogs();
  fetchHistoricalLogs(); // Geçmiş logları da ilk yüklemede getir
  
  const diagnosticsIntervalId = setInterval(() => {
    fetchDiagnostics();
  }, 30000); // 30 saniyede bir güncelle
  
  const logsIntervalId = setInterval(() => {
    fetchSystemLogs();
  }, 100000); // 10 saniyede bir güncelle
  
  return () => {
    clearInterval(diagnosticsIntervalId);
    clearInterval(logsIntervalId);
  };
}, []);

// İlk useEffect'in hemen altına şunu ekleyin

// Filtreler değiştiğinde logları tekrar yükle
useEffect(() => {
  // Her filtre değişikliğinde çağrılacak, boş string bile bir değişiklik olarak algılanacak
  console.log('Filtreler değişti:', { selectedDate, selectedLevel, selectedSource });
  fetchHistoricalLogs();
}, [selectedDate, selectedLevel, selectedSource]);
  const fetchDiagnostics = async () => {
    try {
      setLoading(true);
      const data = await systemService.getDiagnostics();
      
      // Veri yapısını console'a yazdır
      console.log('Diagnostics API yanıtı:', JSON.stringify(data, null, 2));
      
      setDiagnostics(data);
      setLastUpdated(new Date().toLocaleTimeString());
      setError(null);
    } catch (err) {
      console.error('Diagnostik verileri alınamadı:', err);
      setError('Sunucu diagnostik verileri alınamadı.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSystemLogs = async () => {
    try {
      setLogsLoading(true);
      const data = await systemService.getSystemLogs();
      setSystemLogs(data.logs);
      setLogsError(null);
    } catch (err) {
      console.error('Sistem log verileri alınamadı:', err);
      setLogsError('Sistem log verileri alınamadı.');
    } finally {
      setLogsLoading(false);
    }
  };

  // İlk yükleme ve düzenli güncellemeler için
  useEffect(() => {
    fetchDiagnostics();
    fetchSystemLogs();
    
    const diagnosticsIntervalId = setInterval(() => {
      fetchDiagnostics();
    }, 30000); // 30 saniyede bir güncelle
    
    const logsIntervalId = setInterval(() => {
      fetchSystemLogs();
    }, 15000); // 15 saniyede bir log verilerini güncelle
    
    return () => {
      clearInterval(diagnosticsIntervalId);
      clearInterval(logsIntervalId);
    };
  }, []);
  
  // Bellek kullanımını MB cinsinden göster
  // Not: Backend'den gelen değerler zaten MB cinsinden olduğundan dönüştürme yapmaya gerek yok
  const formatMemory = (value: number) => {
    return value.toFixed(2);
  };
  
  // Milisaniyeyi okunabilir formata çevir
  const formatUptime = (milliseconds: number) => {
    // Milisaniyeyi saniyeye çevir
    const seconds = milliseconds / 1000;
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    return `${days}g ${hours}s ${minutes}d ${secs}s`;
  };
  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
        <div
          className="flex"
          style={{
            background: 'linear-gradient(to right, #047857, #10b981)',
          }}
        >
          <div className="p-8 flex justify-between items-start relative w-full">
            <div className="space-y-3 flex-1">
              <div className="text-white/80 text-sm font-medium">
                Sistem Analitik Paneli
              </div>
              <h1 className="text-3xl font-bold text-white">
                Sunucu Performans ve İzleme
              </h1>
              <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                Sunucu durumunu, bellek kullanımını, WebSocket bağlantılarını ve
                sistem loglarını gerçek zamanlı olarak izleyin. Geçmiş log kayıtlarını
                analiz edin ve sistem performansını optimize edin.
              </p>
            </div>
            <div
              className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <BarChart3 className="w-20 h-20 text-white" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div>
          <button
            onClick={fetchDiagnostics}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
          >
            Yenile
          </button>
        </div>
        {!loading && lastUpdated && (
          <div className="text-sm text-muted-foreground">
            Son güncelleme: {lastUpdated}
          </div>
        )}
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md border border-red-200">
          {error}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sunucu Durumu */}
        <div className="bg-card rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Sunucu Durumu</h2>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : diagnostics ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Çalışma Süresi
                </label>
                <div className="text-lg font-semibold">
                  {diagnostics?.diagnostics?.uptime ? formatUptime(diagnostics.diagnostics.uptime) : 'Veri bulunamadı'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Bellek Kullanımı (Heap)
                </label>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${Math.min(((diagnostics.diagnostics?.memory?.heapUsed || 0) / (diagnostics.diagnostics?.memory?.heapTotal || 1)) * 100, 100)}%` }}
                  />
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {formatMemory(diagnostics.diagnostics?.memory?.heapUsed || 0)} MB / {formatMemory(diagnostics.diagnostics?.memory?.heapTotal || 0)} MB
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Bellek Kullanımı (RSS)
                </label>
                <div className="text-lg font-semibold">
                  {formatMemory(diagnostics.diagnostics?.memory?.rss || 0)} MB
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Bağlantı İstatistikleri */}
        <div className="bg-card rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Bağlantı İstatistikleri</h2>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : diagnostics ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Aktif WebSocket Bağlantıları</span>
                <span className="font-semibold">{diagnostics.diagnostics?.wsConnections || 0}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span>Toplam Aktif Handler</span>
                <span className="font-semibold">{diagnostics.diagnostics?.total || 0}</span>
              </div>
              
              {Object.entries(diagnostics.diagnostics?.byType || {}).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span>{type} tipi</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* WebSocket Detayları */}
        <div className="bg-card rounded-lg shadow p-6 col-span-1 md:col-span-2">
          <h2 className="text-xl font-semibold mb-4">WebSocket Bağlantı Detayları</h2>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : diagnostics?.diagnostics?.webSocketDetails && diagnostics.diagnostics?.webSocketDetails.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 text-left">Kullanıcı/Cihaz</th>
                    <th className="p-2 text-left">IP Adresi</th>
                    <th className="p-2 text-left">Durum</th>
                    <th className="p-2 text-left">Bağlantı Zamanı</th>
                    <th className="p-2 text-left">Süre</th>
                    <th className="p-2 text-left">Alınan/Gönderilen</th>
                    <th className="p-2 text-left">Detaylar</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnostics.diagnostics?.webSocketDetails?.map((ws, index) => (
                    <tr key={index} className="border-t border-border hover:bg-muted/20">
                      <td className="p-2">
                        <div className="font-medium">{ws.userId || 'Bilinmeyen Kullanıcı'}</div>
                        <div className="text-xs text-muted-foreground">{ws.deviceInfo || 'Bilinmeyen Cihaz'}</div>
                      </td>
                      <td className="p-2">
                        <div>{ws.remoteAddress}</div>
                        <div className="text-xs text-muted-foreground">Port: {ws.port}</div>
                      </td>
                      <td className="p-2">
                        <span className={`px-2 py-1 ${ws.readyState === 1 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'} rounded-full text-xs`}>
                          {ws.readyStateText || (ws.readyState === 1 ? 'AÇIK' : 'KAPALI')}
                        </span>
                      </td>
                      <td className="p-2">{new Date(ws.connectedAt).toLocaleString()}</td>
                      <td className="p-2">{ws.connectionDuration ? `${ws.connectionDuration} sn` : '-'}</td>
                      <td className="p-2">
                        <div className="text-xs"><span className="font-medium">Alınan:</span> {ws.bytesReceived || '0'}</div>
                        <div className="text-xs"><span className="font-medium">Gönderilen:</span> {ws.bytesSent || '0'}</div>
                      </td>
                      <td className="p-2">
                        <button
                          onClick={() => {
                            alert(
                              `Kullanıcı: ${ws.userId || 'Bilinmiyor'}\n` +
                              `Cihaz: ${ws.deviceInfo || 'Bilinmiyor'}\n` +
                              `Tarayıcı: ${ws.userAgent || 'Bilinmiyor'}\n` +
                              `Protokol: ${ws.protocol || 'ws'}\n` +
                              `Path: ${ws.path || '/'}\n` +
                              `Son Aktivite: ${ws.lastActivity ? new Date(ws.lastActivity).toLocaleString() : 'Bilinmiyor'}\n` +
                              `Port: ${ws.port || '-'}`
                            );
                          }}
                          className="p-1 bg-muted text-primary hover:bg-primary/10 rounded-md text-xs"
                        >
                          Detaylar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center p-8 text-muted-foreground">
              {(diagnostics?.diagnostics?.wsConnections ?? 0) > 0 ? 
                `${diagnostics?.diagnostics?.wsConnections ?? 0} aktif WebSocket bağlantısı var, ancak detay bilgileri mevcut değil.` : 
                'Aktif WebSocket bağlantısı bulunamadı.'
              }
            </div>
          )}
        </div>
        
        {/* Handler Tipi Dağılımı */}
        <div className="bg-card rounded-lg shadow p-6 col-span-1 md:col-span-2">
          <h2 className="text-xl font-semibold mb-4">Aktif Handler Dağılımı</h2>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : diagnostics ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(diagnostics.diagnostics?.byType || {}).map(([type, count]) => (
                <div key={type} className="bg-muted/50 p-4 rounded-lg">
                  <div className="text-lg font-semibold">{type}</div>
                  <div className="text-2xl font-bold mt-2">{count}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {((count / (diagnostics.diagnostics?.total || 1)) * 100).toFixed(1)}% toplam handler
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-6 text-muted-foreground">
              Handler bilgileri bulunamadı.
            </div>
          )}
        </div>
        
        {/* Sistem Logları ve Hataları */}
        <div className="bg-card rounded-lg shadow p-6 col-span-1 md:col-span-2 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Sistem Logları ve Hata Kayıtları</h2>
            <button 
              onClick={fetchSystemLogs}
              className="px-3 py-1 bg-primary/80 text-white rounded-md hover:bg-primary transition-colors text-sm"
            >
              Logları Yenile
            </button>
          </div>
          
          {logsError && (
            <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md border border-red-200">
              {logsError}
            </div>
          )}
          
          {logsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : systemLogs && systemLogs.length > 0 ? (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="p-3 text-left">Zaman</th>
                    <th className="p-3 text-left">Seviye</th>
                    <th className="p-3 text-left">Kaynak</th>
                    <th className="p-3 text-left">Mesaj</th>
                    <th className="p-3 text-left">Detaylar</th>
                  </tr>
                </thead>
                <tbody>
                  {systemLogs.map((log, index) => (
                    <tr key={index} className={`border-t border-border ${log.level === 'error' || log.level === 'critical' ? 'bg-red-50' : log.level === 'warning' ? 'bg-yellow-50' : ''}`}>
                      <td className="p-3 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          log.level === 'error' ? 'bg-red-100 text-red-800' : 
                          log.level === 'critical' ? 'bg-red-200 text-red-900' : 
                          log.level === 'warning' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-green-100 text-green-800'
                        }`}>
                          {log.level === 'critical' ? 'KRİTİK' : 
                           log.level === 'error' ? 'HATA' : 
                           log.level === 'warning' ? 'UYARI' : 
                           'BİLGİ'
                          }
                        </span>
                      </td>
                      <td className="p-3 text-sm">{log.source}</td>
                      <td className="p-3 text-sm">{log.message}</td>
                      <td className="p-3 font-mono text-xs max-w-md truncate">
                        {log.details ? (
                          <div className="cursor-pointer hover:underline" title={typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}>
                            {typeof log.details === 'object' ? JSON.stringify(log.details).substring(0, 50) + '...' : log.details.substring(0, 50)}
                          </div>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center p-6 text-muted-foreground">
              Sistem logları bulunamadı.
            </div>
          )}





          
        </div>

        {/* Sistem Logları bölümünün kapanış div'inden sonra şunu ekleyin (yaklaşık satır 314 civarı) */}

{/* Geçmiş Loglar Bölümü */}
<div className="bg-card rounded-lg shadow p-6 col-span-1 md:col-span-2 mt-6">
  <div className="flex justify-between items-center mb-4">
    <h2 className="text-xl font-semibold">Geçmiş Log Kayıtları</h2>
    <div className="flex space-x-2">
      <select 
        className="px-3 py-1 bg-muted rounded-md text-sm border border-border"
        value={selectedDate || ''}
        onChange={(e) => setSelectedDate(e.target.value)}
      >
        <option value="">Bugün</option>
        {availableDates.map(date => (
          <option key={date} value={date}>{date}</option>
        ))}
      </select>
      
      <select 
        className="px-3 py-1 bg-muted rounded-md text-sm border border-border"
        value={selectedLevel || ''}
        onChange={(e) => setSelectedLevel(e.target.value)}
      >
        <option value="">Tüm Seviyeler</option>
        <option value="info">Bilgi</option>
        <option value="warning">Uyarı</option>
        <option value="error">Hata</option>
        <option value="critical">Kritik</option>
      </select>
      
      <select 
        className="px-3 py-1 bg-muted rounded-md text-sm border border-border"
        value={selectedSource || ''}
        onChange={(e) => setSelectedSource(e.target.value)}
      >
        <option value="">Tüm Kaynaklar</option>
        {availableSources.map(source => (
          <option key={source} value={source}>{source}</option>
        ))}
      </select>
      
      <button 
        onClick={fetchHistoricalLogs}
        className="px-3 py-1 bg-primary/80 text-white rounded-md hover:bg-primary transition-colors text-sm"
      >
        Filtrele
      </button>
    </div>
  </div>
  
  {historicalLogsError && (
    <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md border border-red-200">
      {historicalLogsError}
    </div>
  )}
  
  {historicalLogsLoading ? (
    <div className="flex justify-center items-center h-32">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
    </div>
  ) : historicalLogs && historicalLogs.logs.length > 0 ? (
    <div className="overflow-x-auto max-h-96 overflow-y-auto">
      <table className="w-full">
        <thead className="bg-muted sticky top-0">
          <tr>
            <th className="p-3 text-left">Zaman</th>
            <th className="p-3 text-left">Seviye</th>
            <th className="p-3 text-left">Kaynak</th>
            <th className="p-3 text-left">Mesaj</th>
            <th className="p-3 text-left">Detaylar</th>
          </tr>
        </thead>
        <tbody>
          {historicalLogs.logs.map((log: SystemLog, index: number) => (
            <tr key={index} className={`border-t border-border ${
              log.level === 'error' || log.level === 'critical' ? 'bg-red-50' : 
              log.level === 'warning' || log.level === 'warn' ? 'bg-yellow-50' : ''
            }`}>
              <td className="p-3 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
              <td className="p-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  log.level === 'error' ? 'bg-red-100 text-red-800' : 
                  log.level === 'critical' ? 'bg-red-200 text-red-900' : 
                  log.level === 'warning' || log.level === 'warn' ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-green-100 text-green-800'
                }`}>
                  {log.level === 'critical' ? 'KRİTİK' : 
                   log.level === 'error' ? 'HATA' : 
                   log.level === 'warning' || log.level === 'warn' ? 'UYARI' : 
                   'BİLGİ'
                  }
                </span>
              </td>
              <td className="p-3 text-sm">{log.source}</td>
              <td className="p-3 text-sm">{log.message}</td>
              <td className="p-3 font-mono text-xs max-w-md truncate">
                {log.details ? JSON.stringify(log.details) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="text-center p-6 text-muted-foreground">
      {historicalLogs ? 'Bu tarih için log kaydı bulunamadı.' : 'Geçmiş log kayıtları yüklenemedi.'}
    </div>
  )}
  
  {historicalLogs && historicalLogs.limited && (
    <div className="text-center p-2 mt-2 text-sm text-muted-foreground">
      Not: Maksimum görüntülenen log sayısına ulaşıldı. Daha fazla görmek için filtreleme kullanın.
    </div>
  )}
</div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsPage
